import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { api } from '../../../lib/api-client';
import {
  WS_EVENTS,
  type Level4DetectedPayload,
  type QualitySignalsUpdatedPayload,
} from '../../../lib/websocket/events';
import { useWebSocket } from '../../../lib/websocket/WebSocketProvider';

export interface QualitySignalsState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  currentSentiment: 'positive' | 'neutral' | 'negative' | 'hostile' | null;
  sentimentTrend: 'ascending' | 'stable' | 'descending' | null;
  sensitivityLevel: 1 | 2 | 3 | 4 | null;
  primaryIntent: string | null;
  lastClassifiedAt: string | null;
  /** Última detecção de Nível 4 — UI pode destacar visualmente. */
  lastLevel4?: { detectedAt: string; primaryIntent: string };
}

/**
 * useQualitySignals — Fatia 8.
 *
 * Carrega estado atual de QualitySignals via REST + subscribe WS para updates
 * de classification e level-4 detected.
 */
export function useQualitySignals(conversationId: string | undefined): QualitySignalsState {
  const queryClient = useQueryClient();
  const { subscribe } = useWebSocket();

  const queryKey = ['quality-signals', conversationId];
  const queryResult = useQuery({
    queryKey,
    enabled: conversationId !== undefined,
    queryFn: () => {
      if (conversationId === undefined) return Promise.resolve(null);
      return api.getQualitySignals(conversationId);
    },
  });

  useEffect(() => {
    if (conversationId === undefined) return;
    const unsubUpdate = subscribe<QualitySignalsUpdatedPayload>(
      WS_EVENTS.QUALITY_SIGNALS_UPDATED,
      (envelope) => {
        if (envelope.data.conversationId !== conversationId) return;
        void queryClient.invalidateQueries({ queryKey });
      },
    );
    const unsubLevel4 = subscribe<Level4DetectedPayload>(WS_EVENTS.LEVEL_4_DETECTED, (envelope) => {
      if (envelope.data.conversationId !== conversationId) return;
      void queryClient.invalidateQueries({ queryKey });
    });
    return () => {
      unsubUpdate();
      unsubLevel4();
    };
  }, [conversationId, queryClient, subscribe, queryKey]);

  const data = queryResult.data;
  let status: QualitySignalsState['status'];
  if (queryResult.isError) status = 'error';
  else if (queryResult.isLoading) status = 'loading';
  else if (data) status = 'ready';
  else status = 'idle';

  const sensitivityLevel =
    data?.sensitivityLevel !== null && data?.sensitivityLevel !== undefined
      ? (data.sensitivityLevel as 1 | 2 | 3 | 4)
      : null;

  return {
    status,
    currentSentiment: (data?.currentSentiment as QualitySignalsState['currentSentiment']) ?? null,
    sentimentTrend: data?.sentimentTrend ?? null,
    sensitivityLevel,
    primaryIntent: data?.primaryIntent ?? null,
    lastClassifiedAt: data?.lastClassifiedAt ?? null,
  };
}
